"use client";

import Image from "next/image";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { ShieldCheck, Landmark, Scale, Activity } from "lucide-react";

// ── Data ────────────────────────────────────────────────────────────────────

const PILLARS = [
  {
    icon: ShieldCheck,
    text: "Backed by real 24K physical gold",
  },
  {
    icon: Landmark,
    text: "Stored in high security Dubai vaults, fully insured",
  },
  {
    icon: Scale,
    text: "Shariah compliant and completely interest free",
  },
  {
    icon: Activity,
    text: "Real time pricing on every trade",
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

function fadeSide(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, x: reduce ? 0 : 32 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Section ─────────────────────────────────────────────────────────────────

export function AboutStory() {
  const reduce = useReducedMotion() ?? false;

  return (
    <motion.section
      className="bg-neutral-0 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="about-story-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={container}
    >
      <div className="mx-auto grid max-w-[1200px] items-center gap-10 px-6 sm:px-10 lg:grid-cols-2 lg:gap-16 lg:px-8">
        {/* Left: narrative */}
        <div>
          <motion.p
            variants={fadeUp(reduce)}
            className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500"
          >
            Why we exist
          </motion.p>
          <motion.h2
            id="about-story-heading"
            variants={fadeUp(reduce)}
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
          >
            From family tradition to a tap on your phone.
          </motion.h2>

          <motion.p
            variants={fadeUp(reduce)}
            className="mt-5 text-base leading-relaxed text-text-body"
          >
            For generations, families across the region have trusted gold to
            protect their wealth. But owning it often meant large sums, storage
            worries, and paperwork. We built JustGold to change that.
          </motion.p>
          <motion.p
            variants={fadeUp(reduce)}
            className="mt-4 text-base leading-relaxed text-text-body"
          >
            Now anyone can own real 24K gold from their phone, backed by physical
            metal held in insured Dubai vaults. Every transaction is recorded on
            chain for complete transparency, so what you see is exactly what you
            own.
          </motion.p>

          {/* Pillars */}
          <motion.ul
            variants={container}
            className="mt-8 flex flex-col gap-3"
          >
            {PILLARS.map(({ icon: Icon, text }) => (
              <motion.li
                key={text}
                variants={fadeUp(reduce)}
                className="flex items-start gap-3"
              >
                <span
                  className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600"
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <span className="text-[15px] leading-snug text-text-heading">
                  {text}
                </span>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        {/* Right: image card */}
        <motion.div
          variants={fadeSide(reduce)}
          className="relative"
        >
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-card border border-[#e3ecfb] bg-gradient-to-b from-[#fafbff] to-[#eef3fb]">
            <Image
              src="/about-team-gold.png"
              alt="JustGold team discussing gold ownership and market growth around a table"
              fill
              sizes="(max-width: 1024px) 90vw, 560px"
              className="object-cover object-center"
            />
            {/* Floating trust chip */}
            <div className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/80 px-4 py-2 backdrop-blur-md shadow-[0_4px_18px_rgba(14,58,138,0.08)]">
              <ShieldCheck
                className="h-4 w-4 text-primary-600"
                strokeWidth={2}
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-text-heading">
                Vaulted in Dubai · Fully insured
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}
